import { Typography } from '@material-ui/core';
import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight } from 'react-feather';
import { useStringQueryParam } from '../../hooks/useStringQueryParam';
import styled from '../../styled';
import { DemoButton } from '../DemoButton';
import { Headline } from '../Headline';
import Section from '../Section';

const SectionWrapper = styled('section')`
  padding: 100px 24px;
  margin: 0 auto;

  @media (max-width: 960px) {
    padding: 80px 24px;
  }
`;

const HeroSection = styled('div')`
  text-align: center;

  @media (max-width: 960px) {
    margin-bottom: 60px;
  }
`;

const Subheadline = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 24px auto 32px;
  max-width: 800px;
`;

const SectionHeader = styled('div')`
  margin-bottom: 40px;
`;

const SectionHeaderText = styled('h3')`
  font-size: 24px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0 0 32px 0;
`;

const TabsContainer = styled('div')`
  justify-content: center;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 60px;
  margin-top: 30px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const Tab = styled('button')<{ active: boolean }>`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => (p.active ? '#ff6b35' : '#4f4f4f')};
  background-color: ${(p) => (p.active ? '#fff2e8' : 'transparent')};
  border: 2px solid ${(p) => (p.active ? '#ff6b35' : '#e2e8f0')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }
`;

const ContentWrapper = styled('div')`
  max-width: 800px;
  margin: 0 auto;
`;

const BulletList = styled('ul')<{ gap?: string; marginBottom?: string }>`
  list-style: none;
  padding: 0;
  margin: 0 0 ${(p) => p.marginBottom || '0'} 0;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.gap || '12px'};
`;

const BulletItem = styled('li')`
  font-size: 16px;
  line-height: 1.6;
  color: #4f4f4f;
  padding-left: 24px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    top: -6px;
    color: #ff6b35;
    font-weight: bold;
    font-size: 20px;
  }
`;

const ProductHighlights = styled('div')`
  margin-top: 40px;
`;

const ProductHighlightsTitle = styled('h4')`
  font-size: 18px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0 0 20px 0;
`;

const CtaSection = styled('div')`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 40px;
  flex-wrap: wrap;
`;

const SeeAllFeaturesLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #ff6b35;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const LogosWrapper = styled('div')`
  padding: 60px 24px 40px;
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
`;

const LogosContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto 36px;

  @media (max-width: 960px) {
    justify-content: center;
    gap: 32px;
  }
`;

const LOGO_HEIGHT = 24;

const Logo = styled('img')`
  height: ${LOGO_HEIGHT}px;
  opacity: 0.6;
  transition: opacity 0.2s, filter 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }

  @media (max-width: 960px) {
    height: 32px;
  }

  @media (max-width: 600px) {
    height: 28px;
  }
`;

const logos = [
  {
    src: '/images/logos/usa-today.svg',
    alt: 'USA Today',
    width: 100,
    height: 36
  },
  {
    src: '/images/logos/outside-inc.svg',
    alt: 'Outside Inc',
    width: 100,
    height: 36
  },
  {
    src: '/images/logos/news-uk.svg',
    alt: 'News UK',
    width: 100,
    height: 36
  },
  {
    src: '/images/logos/theskimm.svg',
    alt: 'theSkimm',
    width: 100,
    height: 36
  },
  {
    src: '/images/logos/katie-couric-media.svg',
    alt: 'Katie Couric Media',
    width: 100,
    height: 36
  },
  {
    src: '/images/logos/business-insider.svg',
    alt: 'Business Insider',
    width: 100,
    height: 36
  }
];

const useCases = [
  {
    id: 'news-media',
    title: 'News & Media Publishers',
    tagline: 'From content to commerce: powered by real purchase data.',
    description:
      'Affilimate delivers the most valuable intent signal in modern media: what audiences actually buy. By uncovering exactly how readers interact, shop, and convert across every article and traffic source we help publishers understand the true value of every reader — revealing which content, products, and campaigns drive real, measurable profit.',
    benefits: [
      'Audience development ties purchase behavior to subscribers — revealing who converts, which segments drive profit, and where to spend smarter.',
      "Ad sales builds audience packages based on verified buyers, creating high-value segments brands can't find anywhere else.",
      'Editorial learns which stories, merchants, and placements audiences prefer and transact on — guiding content toward what truly earns.'
    ],
    result:
      'The result is a connected business where every decision — from content strategy to audience acquisition — is driven by purchase-backed revenue intelligence.',
    productHighlights: [
      'Real purchase data tied to audience segments and campaign performance.',
      'Intelligent analytics reveal which content and merchants drive profitability.',
      'Unified tracking across affiliate, sponsorship, and commerce campaigns.',
      'Automated link management and reconciliation eliminate manual work.'
    ],
    screenshot: undefined as string | undefined,
    productHighlightsScreenshot: '/images/features/new-heatmaps.png'
  },
  {
    id: 'newsletter',
    title: 'Newsletter',
    tagline: 'From send to sale: finally measurable.',
    description:
      'Affilimate gives newsletter teams full visibility into how email drives affiliate revenue, at scale. Track revenue per campaign or segment, connect subscriber data to conversions, and enrich your CRM with granular performance insights. With clean, ESP-compatible links and flexible custom dimensions, every send becomes a measurable driver of growth.',
    benefits: [],
    result: '',
    productHighlights: [
      'Track revenue per send, segment, or subscriber',
      'Connect custom dimensions for richer insights.',
      'Enrich your CRM with conversion data.',
      'Create ESP-safe links with the built-in shortener.'
    ],
    productHighlightsScreenshot: '/images/screenshots/newsletter-example.png'
  },
  {
    id: 'shopping-loyalty',
    title: 'Shopping & Loyalty Platforms',
    tagline: 'From clicks to commissions: unified and ready-to-use.',
    description:
      'Affilimate gives shopping, loyalty, and cashback platforms a centralized source of truth for affiliate revenue across every network and merchant. With real-time APIs, user-level attribution, and automated link creation, you can power rewards, recommendations, and commerce experiences with reliable, scalable affiliate infrastructure.',
    benefits: [],
    result: '',
    productHighlights: [
      'Unified affiliate dashboard across all networks',
      'Custom dimensions for user-level attribution',
      'Real-time link creation and tracking APIs',
      'Payout reporting and reconciliation',
      'Historical views for audit and accuracy',
      'Currency and status normalization for global programs'
    ],
    screenshot: undefined as string | undefined,
    productHighlightsScreenshot:
      '/images/features/affiliate-dashboard/transaction-details.png'
  },
  {
    id: 'finance',
    title: 'Finance teams',
    tagline: 'From transaction to payment: finally a clear picture.',
    description:
      "Affilimate gives finance teams instant clarity from transaction to payment — eliminating manual spreadsheets, inconsistent statuses, and weeks-long month-end closes. With unified data across your affiliate networks, you'll always know what's been earned, paid, and outstanding.",
    benefits: [],
    result: '',
    productHighlights: [
      'Real-time sub-ledger with transaction-level detail',
      "Status timeline: See each transaction's full lifecycle",
      'Bonus filtering: Separate IO-backed bonuses from commissions',
      'Historical views: What was owed at any past point in time',
      'Automated payout reconciliation: Match payouts to transactions in seconds'
    ],
    screenshot: undefined as string | undefined,
    productHighlightsScreenshot: '/images/features/paid-status-reporting.png'
  }
];

export const UseCasesSection = () => {
  const [activeTabParam, setActiveTab] = useStringQueryParam(
    'tab',
    'news-media'
  );

  // Validate that the tab from URL is a valid use case ID
  const validTabIds = useCases.map((uc) => uc.id);
  const isValidTab = validTabIds.includes(activeTabParam);
  const activeTab = isValidTab ? activeTabParam : 'news-media';

  // If the URL param was invalid, update it to the default
  React.useEffect(() => {
    if (activeTabParam && !isValidTab) {
      setActiveTab('news-media');
    }
  }, [activeTabParam, isValidTab, setActiveTab]);

  const activeUseCase =
    useCases.find((uc) => uc.id === activeTab) || useCases[0];

  return (
    <>
      <SectionWrapper>
        <HeroSection>
          <Headline
            component="h1"
            variant="h1"
            style={{ maxWidth: '800px', margin: '0 auto' }}
          >
            Growth Tools and Infrastructure for Affiliate Publishers
          </Headline>
          <Subheadline>
            Affilimate redefines affiliate data as revenue intelligence —
            pairing advanced automation with performance analytics to increase
            yield, margin, and brand outcomes.
          </Subheadline>
          <Subheadline>
            <em>Trusted by leading publishers like:</em>
          </Subheadline>
          <LogosContainer>
            {logos.map((logo) => (
              <Logo key={logo.src} src={logo.src} alt={logo.alt} />
            ))}
          </LogosContainer>
          <DemoButton text="Book a demo" variant="contained" />
        </HeroSection>
        <SectionHeader style={{ textAlign: 'center' }}>
          <TabsContainer>
            {useCases.map((useCase) => (
              <Tab
                key={useCase.id}
                active={activeTab === useCase.id}
                onClick={() => setActiveTab(useCase.id)}
              >
                {useCase.title}
              </Tab>
            ))}
          </TabsContainer>
        </SectionHeader>

        {/* First Section: Title, tagline, description, and benefits */}
        {activeUseCase.screenshot ? (
          <Section
            image={activeUseCase.screenshot}
            variant="screenshot"
            orientation="right"
            headline={
              <>
                <Headline component="h3" variant="h3">
                  {activeUseCase.title}
                </Headline>
                <Typography
                  variant="body1"
                  style={{ marginTop: 16, marginBottom: 16 }}
                >
                  {activeUseCase.tagline}
                </Typography>
              </>
            }
            body={
              <>
                <Typography variant="body1" paragraph>
                  {activeUseCase.description}
                </Typography>

                {activeUseCase.benefits.length > 0 && (
                  <>
                    <Typography variant="body1" paragraph>
                      When revenue data becomes intelligence, every team wins:
                    </Typography>
                    <BulletList gap="16px" marginBottom="40px">
                      {activeUseCase.benefits.map((benefit, index) => (
                        <BulletItem key={index}>{benefit}</BulletItem>
                      ))}
                    </BulletList>
                    {activeUseCase.result && (
                      <Typography variant="body1" paragraph>
                        {activeUseCase.result}
                      </Typography>
                    )}
                  </>
                )}
              </>
            }
          />
        ) : (
          <ContentWrapper>
            <Headline component="h2" variant="h2">
              {activeUseCase.title}
            </Headline>
            <Typography
              variant="body1"
              style={{ marginTop: 16, marginBottom: 16, fontWeight: 600 }}
            >
              {activeUseCase.tagline}
            </Typography>
            <Typography variant="body1" paragraph>
              {activeUseCase.description}
            </Typography>

            {activeUseCase.benefits.length > 0 && (
              <>
                <Typography variant="body1" paragraph>
                  When revenue data becomes intelligence, every team wins:
                </Typography>
                <BulletList gap="16px" marginBottom="40px">
                  {activeUseCase.benefits.map((benefit, index) => (
                    <BulletItem key={index}>{benefit}</BulletItem>
                  ))}
                </BulletList>
                {activeUseCase.result && (
                  <Typography variant="body1" paragraph>
                    {activeUseCase.result}
                  </Typography>
                )}
              </>
            )}
          </ContentWrapper>
        )}

        {/* Second Section: Product highlights */}
        {activeUseCase.productHighlightsScreenshot ? (
          <Section
            image={activeUseCase.productHighlightsScreenshot}
            variant="screenshot"
            orientation="right"
            headline={
              <Headline component="h3" variant="h3">
                Product highlights:
              </Headline>
            }
            body={
              <>
                <BulletList gap="12px">
                  {activeUseCase.productHighlights.map((highlight, index) => (
                    <BulletItem key={index}>{highlight}</BulletItem>
                  ))}
                </BulletList>
                <CtaSection>
                  <DemoButton text="Book a demo" variant="contained" />
                  <SeeAllFeaturesLink to="/product/">
                    See all features <ArrowRight size={18} />
                  </SeeAllFeaturesLink>
                </CtaSection>
              </>
            }
          />
        ) : (
          <ContentWrapper>
            <ProductHighlights>
              <Headline component="h3" variant="h3">
                Product highlights:
              </Headline>
              <BulletList gap="12px">
                {activeUseCase.productHighlights.map((highlight, index) => (
                  <BulletItem key={index}>{highlight}</BulletItem>
                ))}
              </BulletList>
            </ProductHighlights>
            <CtaSection>
              <DemoButton text="Book a demo" variant="contained" />
              <SeeAllFeaturesLink to="/product/">
                See all features <ArrowRight size={18} />
              </SeeAllFeaturesLink>
            </CtaSection>
          </ContentWrapper>
        )}
      </SectionWrapper>
    </>
  );
};
